import { assets, imageByPath, type ImageAsset } from '@/assets';

export interface Foe {
  id: string;
  codename: string;
  /**
   * The codename as the child says it when transmitting it (IPA, General American; a vocalic r as
   * vowel + ɹ, as in the practice words), judged on its first /ɹ/.
   */
  spoken: readonly string[];
  portraits: {
    /** In disguise, while the codename is still being uncovered. */
    disguised: ImageAsset;
    /** Unmasked and cheerful. */
    revealed: ImageAsset;
  };
}

/** A character portrait by path, e.g. "cipher-cat/disguised", falling back to `fallback`. */
function portrait(path: string, fallback: ImageAsset): ImageAsset {
  return imageByPath[`characters/${path}`] ?? fallback;
}

export const shadowFox: Foe = {
  id: 'shadow-fox',
  codename: 'RED SHADOW FOX',
  spoken: ['ɹ', 'ɛ', 'd', 'ʃ', 'æ', 'd', 'oʊ', 'f', 'ɑ', 'k', 's'],
  portraits: {
    disguised: assets.characters.shadowFox.disguised,
    revealed: assets.characters.shadowFox.happy,
  },
};

function foe(id: string, codename: string, spoken: string): Foe {
  const disguised = portrait(`${id}/disguised`, shadowFox.portraits.disguised);
  return {
    id,
    codename,
    spoken: spoken.split(' '),
    portraits: { disguised, revealed: portrait(`${id}/revealed`, disguised) },
  };
}

/**
 * The Foe Elevator's foes, one per floor (see content/floors.ts). Higher floors have longer
 * codenames (9 to 16 letters) to uncover, and every added title has an /r/ in it.
 */
export const foes = {
  cipherCat: foe('cipher-cat', 'CIPHER CAT', 's aɪ f ə ɹ k æ t'),
  agentRook: foe('agent-rook', 'AGENT ROOK', 'eɪ dʒ ə n t ɹ ʊ k'),
  shadowFox,
  echoLynx: foe('echo-lynx', 'SECRET ECHO LYNX', 's i k ɹ ə t ɛ k oʊ l ɪ ŋ k s'),
  mistyMole: foe('misty-mole', 'DOCTOR MISTY MOLE', 'd ɑ k t ə ɹ m ɪ s t i m oʊ l'),
  novaOwl: foe('nova-owl', 'PROFESSOR NOVA OWL', 'p ɹ ə f ɛ s ə ɹ n oʊ v ə aʊ l'),
} as const;
